import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db, getCollectionName } from './firebase';
import { collection, getDocs, doc, setDoc, getDoc, query, orderBy, resumo } from './firestore-compat';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, LineChart, Line,
} from 'recharts';
import * as XLSX from 'xlsx';
import { detectCol } from './dashboardUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseDateBR(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m1 = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m1) return new Date(+m1[3], +m1[2] - 1, +m1[1]);
  const m2 = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(+m2[1], +m2[2] - 1, +m2[3]);
  return null;
}
const formatDateBR = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d) ? null : d.toLocaleDateString('pt-BR');
};
const getDaysDiff = (start, end) => {
  if (!start || !end) return null;
  const s = start.toDate ? start.toDate() : new Date(start);
  const e = end.toDate ? end.toDate() : new Date(end);
  return Math.max(0, Math.round((e - s) / (1000 * 60 * 60 * 24)));
};
const getMonthLabel = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d) ? null : d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
};
const toMs = (ts) => ts ? (ts.toMillis ? ts.toMillis() : new Date(ts).getTime()) : 0;
const toDateInput = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const nf = v => v === 'Homesenior' ? 'Vitalcare' : (v || '—');
const pct = (n, d) => d ? `${((n/d)*100).toFixed(1)}%` : '—';
const avg = (arr) => arr.length ? (arr.reduce((s,v)=>s+v,0)/arr.length).toFixed(1) : '—';

// ─── Constantes ───────────────────────────────────────────────────────────────
const TIPO_CONFIG = {
  hospitalizacao: { label: 'Hospitalização', emoji: '🟠', color: '#f97316', chartColor: '#fb923c', badgeBg: 'linear-gradient(135deg, #f97316, #ea580c)' },
  implantacao:    { label: 'Implantação',    emoji: '🔵', color: '#3b82f6', chartColor: '#60a5fa', badgeBg: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
};
const SETOR_CONFIG = {
  'PAD':                      { color: '#6366f1', icon: '🩺' },
  'Exames':                   { color: '#f59e0b', icon: '🔬' },
  'Remoções':                 { color: '#ef4444', icon: '🚑' },
  'Mobiliário':               { color: '#8b5cf6', icon: '🛋️' },
  'Gases Medicinais':         { color: '#0ea5e9', icon: '💨' },
  'Equipamentos':             { color: '#d97706', icon: '⚙️' },
  'Prontuário':               { color: '#0891b2', icon: '📋' },
  'Farmácia':                 { color: '#16a34a', icon: '💊' },
  'Logística':                { color: '#7c3aed', icon: '🚚' },
  'Suprimentos':              { color: '#db2777', icon: '📦' },
  'Logística e Suprimentos':  { color: '#7c3aed', icon: '🚚' },
};
const DEFAULT_SLA = { hospitalizacao: 24, implantacao: 48 };
const HOSP_CONDS  = ['Hospitalização','Alta após hospitalização','Mudança H.C após Hospitalização'];
const OBITO_CONDS = ['Óbito','Óbito após hospitalização'];
const CANCEL_CONDS = ['Cancelado pelo convenio','Suspenso pela Operadora','Orçamento Inválido'];
const COLORS6 = ['#6366f1','#f97316','#0ea5e9','#10b981','#f59e0b','#8b5cf6'];
const ALARM_LS_KEY = 'oc_alarm_last_play';
const ALARM_COOLDOWN = 3600000;

const resolveOcTipo = (oc) => {
  const t = oc.paciente?.tipoOcorrencia;
  if (t === 'Implantação') return 'implantacao';
  if (t === 'Hospitalização') return 'hospitalizacao';
  if (oc.tipo === 'implantacao' || oc.tipo === 'Implantação') return 'implantacao';
  if (oc.tipo === 'hospitalizacao' || oc.tipo === 'Hospitalização') return 'hospitalizacao';
  if (oc.captacaoId) return 'implantacao';
  return 'hospitalizacao';
};

// ─── Sound ────────────────────────────────────────────────────────────────────
function playAlarm60s() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    for (let i = 0; i < 240; i++) {
      const t = ctx.currentTime + i * 0.25;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = i % 2 === 0 ? 1300 : 850;
      gain.gain.setValueAtTime(0.35, t);
      gain.gain.setValueAtTime(0.35, t + 0.18);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.23);
      osc.start(t);
      osc.stop(t + 0.23);
    }
    return ctx;
  } catch (e) { return null; }
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────
function SlaModal({ slaConfig, onSave, onClose }) {
  const [hosp, setHosp] = useState(slaConfig.hospitalizacao || 24);
  const [hospUnit, setHospUnit] = useState(slaConfig.hospitalizacaoUnit || 'horas');
  const [impl, setImpl] = useState(slaConfig.implantacao || 48);
  const [implUnit, setImplUnit] = useState(slaConfig.implantacaoUnit || 'horas');
  const toHours = (val, unit) => unit === 'minutos' ? val / 60 : val;
  const unitLabel = (val, unit) => unit === 'minutos' ? `≈ ${val} min` : `≈ ${(val/24).toFixed(1)} dias`;
  const UB = (active) => ({ padding:'4px 10px', borderRadius:6, border:'none', fontSize:11, fontWeight:700, cursor:'pointer', background:active?'#6366f1':'#f1f5f9', color:active?'#fff':'#64748b', transition:'all 0.15s' });
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:'32px 36px', width:420, boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:'Inter,sans-serif' }}>
        <h3 style={{ margin:'0 0 6px', color:'#1e293b', fontSize:'1.1rem' }}>⚙️ Configurar SLA</h3>
        <p style={{ color:'#64748b', fontSize:13, margin:'0 0 24px', lineHeight:1.5 }}>
          Tempo máximo de resposta. Ao ultrapassar, a ocorrência é marcada como <strong style={{ color:'#dc2626' }}>vencida</strong>.
        </p>
        {[
          { label:'🟠 Hospitalização', value:hosp, setter:setHosp, unit:hospUnit, setUnit:setHospUnit },
          { label:'🔵 Implantação',    value:impl, setter:setImpl, unit:implUnit, setUnit:setImplUnit },
        ].map(({ label, value, setter, unit, setUnit }) => (
          <div key={label} style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#475569', marginBottom:8 }}>{label}</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="number" min="1" value={value} onChange={e=>setter(Math.max(1,Number(e.target.value)))}
                style={{ width:80, padding:'9px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:16, fontWeight:700, textAlign:'center', outline:'none', fontFamily:'Inter,sans-serif' }} />
              <div style={{ display:'flex', gap:4 }}>
                <button style={UB(unit==='horas')}     onClick={()=>setUnit('horas')}>horas</button>
                <button style={UB(unit==='minutos')} onClick={()=>setUnit('minutos')}>minutos</button>
              </div>
              <span style={{ fontSize:11, color:'#94a3b8', background:'#f1f5f9', padding:'3px 8px', borderRadius:20 }}>{unitLabel(value,unit)}</span>
            </div>
          </div>
        ))}
        <div style={{ display:'flex', gap:10, marginTop:24 }}>
          <button onClick={onClose} style={{ flex:1, padding:10, borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:600, cursor:'pointer', fontSize:13 }}>Cancelar</button>
          <button onClick={()=>onSave({ hospitalizacao:toHours(hosp,hospUnit), hospitalizacaoUnit:hospUnit, implantacao:toHours(impl,implUnit), implantacaoUnit:implUnit })}
            style={{ flex:2, padding:10, borderRadius:8, border:'none', background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            ✅ Salvar SLA
          </button>
        </div>
      </div>
    </div>
  );
}

function SetorCard({ setor, stats }) {
  const cfg = SETOR_CONFIG[setor] || { color:'#94a3b8', icon:'📁' };
  const p = stats.total > 0 ? Math.round((stats.concluido||0)/stats.total*100) : 0;
  return (
    <div style={{ background:'#fff', borderRadius:14, padding:'16px 20px', boxShadow:'0 2px 10px rgba(0,0,0,0.05)', border:'1px solid #e8ecf4', borderTop:`4px solid ${cfg.color}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <span style={{ fontSize:'1.2rem' }}>{cfg.icon}</span>
        <span style={{ fontWeight:700, fontSize:12, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{setor}</span>
      </div>
      <div style={{ fontSize:'2rem', fontWeight:800, color:cfg.color, lineHeight:1, marginBottom:8 }}>
        {stats.total}<span style={{ fontSize:10, fontWeight:500, color:'#94a3b8', marginLeft:4 }}>tarefas</span>
      </div>
      <div style={{ height:5, background:'#f1f5f9', borderRadius:3, overflow:'hidden', marginBottom:10 }}>
        <div style={{ height:'100%', width:`${p}%`, background:p===100?'#22c55e':cfg.color, borderRadius:3, transition:'width 0.5s' }} />
      </div>
      {[{ label:'Não iniciadas', value:stats.pendente||0, color:'#94a3b8' },
        { label:'Em andamento',  value:stats.em_andamento||0, color:'#3b82f6' },
        { label:'Concluídas',    value:stats.concluido||0, color:'#16a34a' }].map(({label,value,color})=>(
        <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:10, color:'#64748b' }}>{label}</span>
          <span style={{ fontSize:12, fontWeight:700, color }}>{value}</span>
        </div>
      ))}
      <div style={{ fontSize:9, color:'#94a3b8', marginTop:6, textAlign:'right', letterSpacing:'0.03em' }}>{p}% CONCLUÍDO</div>
    </div>
  );
}

// ─── Barra horizontal simples ─────────────────────────────────────────────────
function HBar({ label, value, max, color, suffix='' }) {
  const w = max > 0 ? Math.round((value/max)*100) : 0;
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
        <span style={{ fontSize:'0.78rem', color:'#475569', fontWeight:600, maxWidth:'60%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
        <span style={{ fontSize:'0.78rem', fontWeight:800, color }}>{value}{suffix}</span>
      </div>
      <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${w}%`, background:color, borderRadius:4, transition:'width 0.6s' }} />
      </div>
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon, value, label, color, sub, alert }) {
  return (
    <div style={{
      background:'#fff', borderRadius:14, padding:'18px 20px',
      boxShadow: alert && Number(value)>0 ? '0 2px 10px rgba(220,38,38,0.12)' : '0 2px 10px rgba(0,0,0,0.05)',
      border: alert && Number(value)>0 ? '1.5px solid #fca5a5' : '1px solid #e8ecf4',
      borderLeft:`4px solid ${color}`,
    }}>
      <div style={{ fontSize:'1.2rem', marginBottom:5 }}>{icon}</div>
      <div style={{ fontSize:'1.9rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:'0.72rem', color:'#8a94a6', fontWeight:500, marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:'0.65rem', color:'#b0bec5', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function DashboardHospitalizacao({ initialTipo = 'hospitalizacao' }) {
  // ── Estado ───────────────────────────────────────────────────────────────────
  const [ocorrencias,    setOcorrencias]   = useState([]);
  const [tarefasMap,     setTarefasMap]    = useState({});
  const [loading,        setLoading]       = useState(true);
  const [dataInicio,     setDataInicio]    = useState('');
  const [dataFim,        setDataFim]       = useState(todayStr());
  const [oldestOpenDate, setOldestOpenDate]= useState('');
  const [tipoAtivo,      setTipoAtivo]     = useState(initialTipo);
  const [slaConfig,      setSlaConfig]     = useState(DEFAULT_SLA);
  const [showSlaModal,   setShowSlaModal]  = useState(false);
  const [abaHosp,        setAbaHosp]       = useState('ocorrencias'); // 'ocorrencias' | 'censo' | 'lista'
  const [rowAberta,      setRowAberta]     = useState(null);
  const alarmCtxRef = React.useRef(null);

  // Novos: dados de captação e censo
  const [captacaoPacs, setCaptacaoPacs] = useState([]);
  const [censoRows,    setCensoRows]    = useState([]);
  const [loadingCenso, setLoadingCenso] = useState(false);

  // ── Carrega ocorrências + tarefas ────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const colName = getCollectionName('ocorrencias_v2');
    getDocs(collection(db, colName)).then(async snap => {
      const ocs = snap.docs.map(d => {
        const raw = { id: d.id, ...d.data() };
        raw.tipo = resolveOcTipo(raw);
        return raw;
      });
      setOcorrencias(ocs);
      const abertas = ocs.filter(oc => oc.status !== 'concluido' && oc.dataAbertura);
      if (abertas.length > 0) {
        const oldest = abertas.reduce((min, oc) => toMs(oc.dataAbertura) < toMs(min.dataAbertura) ? oc : min);
        const dt = toDateInput(oldest.dataAbertura);
        setDataInicio(dt); setOldestOpenDate(dt);
      } else {
        setDataInicio(todayStr()); setOldestOpenDate(todayStr());
      }
      const map = {};
      await Promise.all(ocs.map(async oc => {
        try {
          const tSnap = await getDocs(collection(db, colName, oc.id, 'tarefas'));
          map[oc.id] = tSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch { map[oc.id] = []; }
      }));
      setTarefasMap(map);
    }).catch(err => console.warn('Erro ao carregar:', err.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Carrega SLA ───────────────────────────────────────────────────────────────
  useEffect(() => {
    getDoc(doc(db, getCollectionName('configuracoes_v2'), 'sla'))
      .then(d => { if (d.exists()) setSlaConfig({ ...DEFAULT_SLA, ...d.data() }); })
      .catch(() => {});
  }, []);

  // ── Carrega captacao_pacientes ────────────────────────────────────────────────
  useEffect(() => {
    getDocs(query(collection(db, getCollectionName('captacao_pacientes')), resumo('captacao_resumo')))
      .then(snap => setCaptacaoPacs(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  // ── Carrega último censo (indicadores_uploads) ────────────────────────────────
  useEffect(() => {
    setLoadingCenso(true);
    getDocs(query(collection(db, 'indicadores_uploads'), orderBy('timestamp', 'desc')))
      .then(snap => {
        if (snap.empty) { setLoadingCenso(false); return; }
        const latest = snap.docs[0];
        return getDocs(collection(db, 'indicadores_uploads', latest.id, 'rows'))
          .then(rowSnap => {
            setCensoRows(rowSnap.docs.map(d => d.data()).sort((a,b)=>(a._idx||0)-(b._idx||0)));
          });
      })
      .catch(() => {})
      .finally(() => setLoadingCenso(false));
  }, []);

  // ── SLA handler ───────────────────────────────────────────────────────────────
  const handleSaveSla = useCallback(async (newSla) => {
    try {
      await setDoc(doc(db, getCollectionName('configuracoes_v2'), 'sla'), newSla);
      setSlaConfig(newSla);
      setShowSlaModal(false);
    } catch (e) { alert('Erro ao salvar SLA: ' + e.message); }
  }, []);

  // ── Mapas de captação ─────────────────────────────────────────────────────────
  const captacaoById = useMemo(() => {
    const m = {};
    captacaoPacs.forEach(p => { m[p.id] = p; });
    return m;
  }, [captacaoPacs]);

  const captacaoByIW = useMemo(() => {
    const m = {};
    captacaoPacs.forEach(p => {
      const key = String(p.numIW || '').trim();
      if (key) m[key] = p;
    });
    return m;
  }, [captacaoPacs]);

  // ── Normaliza censo ───────────────────────────────────────────────────────────
  const censoNorm = useMemo(() => {
    if (censoRows.length === 0) return [];
    const cols = Object.keys(censoRows[0]);
    const C = {
      status:        detectCol(cols, ['status']),
      condicaoSaida: detectCol(cols, ['condição de saída','condicao de saida','cond saida']),
      operadora:     cols.find(c=>c==='Operadora') ?? detectCol(cols, ['convenio','convênio']),
      complexidade:  detectCol(cols, ['complexidade']),
      nomePaciente:  detectCol(cols, ['nome','paciente']),
      dataInternacao:detectCol(cols, ['data interna','data internacao']),
      dataSaida:     detectCol(cols, ['data da saida','data da saída','saida','saída','alta']),
    };
    return censoRows.map(r => {
      const efCond = String(r[C.condicaoSaida] || '').trim();
      const dSaida = parseDateBR(r[C.dataSaida]);
      const dEntrada = parseDateBR(r[C.dataInternacao]);
      const operadora = String(r[C.operadora] || '').trim();
      const complexidade = String(r[C.complexidade] || '').trim();
      const nome = String(r[C.nomePaciente] || '').trim();
      const statusBruto = String(r[C.status] || '').trim().toLowerCase();
      const ativo = !dSaida && (statusBruto.includes('atendimento') || statusBruto === '' || !C.status);
      const isHosp = HOSP_CONDS.includes(efCond);
      const isObito = OBITO_CONDS.includes(efCond);
      const isCancel = CANCEL_CONDS.includes(efCond);
      return { ...r, _efCond: efCond, _dSaida: dSaida, _dEntrada: dEntrada, _operadora: operadora, _complexidade: complexidade, _nome: nome, _ativo: ativo, _isHosp: isHosp, _isObito: isObito, _isCancel: isCancel };
    });
  }, [censoRows]);

  // ── Ocorrências filtradas + enriquecidas ──────────────────────────────────────
  const ocorrenciasFiltradas = useMemo(() => {
    let list = ocorrencias.filter(oc => resolveOcTipo(oc) === tipoAtivo);
    if (dataInicio) {
      const inicio = new Date(dataInicio + 'T00:00:00');
      list = list.filter(oc => {
        if (!oc.dataAbertura) return true;
        const d = oc.dataAbertura.toDate ? oc.dataAbertura.toDate() : new Date(oc.dataAbertura);
        return d >= inicio;
      });
    }
    if (dataFim) {
      const fim = new Date(dataFim + 'T23:59:59.999');
      list = list.filter(oc => {
        if (!oc.dataAbertura) return true;
        const d = oc.dataAbertura.toDate ? oc.dataAbertura.toDate() : new Date(oc.dataAbertura);
        return d <= fim;
      });
    }
    // Enriquece com dados de captação
    return list.map(oc => {
      const pac = (oc.captacaoId && captacaoById[oc.captacaoId])
        || captacaoByIW[String(oc.paciente?.numIW||'').trim()]
        || null;
      return { ...oc, _pac: pac };
    });
  }, [ocorrencias, tipoAtivo, dataInicio, dataFim, captacaoById, captacaoByIW]);

  // ── IDs vencidas SLA ──────────────────────────────────────────────────────────
  const overdueIds = useMemo(() => {
    const now = Date.now();
    return new Set(
      ocorrenciasFiltradas.filter(oc => {
        if (oc.status === 'concluido') return false;
        const abertura = toMs(oc.dataAbertura);
        if (!abertura || isNaN(abertura)) return false;
        const prazoMs = (slaConfig[resolveOcTipo(oc)] || slaConfig.hospitalizacao || 24) * 3600000;
        return (now - abertura) > prazoMs;
      }).map(oc => oc.id)
    );
  }, [ocorrenciasFiltradas, slaConfig]);

  // ── Alarme ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (loading || overdueIds.size === 0) {
      if (alarmCtxRef.current) { try { alarmCtxRef.current.close(); } catch(e) {} alarmCtxRef.current = null; }
      return;
    }
    const tryPlay = () => {
      const last = Number(localStorage.getItem(ALARM_LS_KEY) || 0);
      if (Date.now() - last >= ALARM_COOLDOWN) {
        localStorage.setItem(ALARM_LS_KEY, String(Date.now()));
        if (alarmCtxRef.current) { try { alarmCtxRef.current.close(); } catch(e) {} }
        alarmCtxRef.current = playAlarm60s();
      }
    };
    tryPlay();
    const interval = setInterval(tryPlay, 60000);
    return () => clearInterval(interval);
  }, [loading, overdueIds.size]);

  // ── KPIs de ocorrências ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total       = ocorrenciasFiltradas.length;
    const emAberto    = ocorrenciasFiltradas.filter(o=>o.status==='aberto').length;
    const emAndamento = ocorrenciasFiltradas.filter(o=>o.status==='em_andamento').length;
    const concluidas  = ocorrenciasFiltradas.filter(o=>o.status==='concluido').length;
    const vencidas    = overdueIds.size;
    const comConc     = ocorrenciasFiltradas.filter(o=>o.status==='concluido'&&o.dataAbertura&&o.dataConclusao);
    const tempoMedio  = comConc.length > 0
      ? Math.round(comConc.reduce((acc,o)=>acc+(getDaysDiff(o.dataAbertura,o.dataConclusao)||0),0)/comConc.length)
      : null;
    const dentroSla   = ocorrenciasFiltradas.filter(o=>!overdueIds.has(o.id)).length;
    const taxaSla     = total ? Math.round((dentroSla/total)*100) : null;
    return { total, emAberto, emAndamento, concluidas, vencidas, tempoMedio, taxaSla };
  }, [ocorrenciasFiltradas, overdueIds]);

  // ── Estatísticas de setores ───────────────────────────────────────────────────
  const setorStats = useMemo(() => {
    const filteredIds = new Set(ocorrenciasFiltradas.map(oc=>oc.id));
    const map = {};
    Object.entries(tarefasMap).forEach(([ocId, tarefas]) => {
      if (!filteredIds.has(ocId)) return;
      tarefas.forEach(t => {
        const setor = t.areaPai || t.area || 'Outros';
        if (!map[setor]) map[setor] = { total:0, pendente:0, em_andamento:0, concluido:0 };
        map[setor].total++;
        const st = t.status || 'pendente';
        map[setor][st] = (map[setor][st]||0)+1;
      });
    });
    return Object.entries(map).sort((a,b)=>b[1].total-a[1].total);
  }, [ocorrenciasFiltradas, tarefasMap]);

  // ── Gráfico por mês ───────────────────────────────────────────────────────────
  const dadosPorMes = useMemo(() => {
    const map = {};
    ocorrenciasFiltradas.forEach(oc => {
      const label = getMonthLabel(oc.dataAbertura);
      if (!label) return;
      map[label] = (map[label]||0)+1;
    });
    return Object.entries(map).map(([mes,total])=>({ mes, total })).slice(-6);
  }, [ocorrenciasFiltradas]);

  // ── Perfil dos pacientes (de captação linkada às ocorrências) ─────────────────
  const perfil = useMemo(() => {
    const linked = ocorrenciasFiltradas.filter(oc=>oc._pac);
    const comVM  = linked.filter(oc=>oc._pac?.vm).length;
    const semVM  = linked.filter(oc=>!oc._pac?.vm).length;

    const porOp = {};
    linked.forEach(oc => {
      const op = nf(oc._pac?.convenio) || oc.paciente?.plano || '—';
      porOp[op] = (porOp[op]||0)+1;
    });

    const porFilial = {};
    linked.forEach(oc => {
      const f = nf(oc._pac?.filial) || '—';
      porFilial[f] = (porFilial[f]||0)+1;
    });

    const diasAte = linked
      .filter(oc=>oc.dataAbertura)
      .map(oc=>{
        const abertura = oc.dataAbertura.toDate ? oc.dataAbertura.toDate() : new Date(oc.dataAbertura);
        return Math.round((Date.now()-abertura.getTime())/(1000*60*60*24));
      });
    const mediaAberto = diasAte.length ? avg(diasAte) : '—';

    return { comVM, semVM, porOp, porFilial, linkedCount: linked.length, mediaAberto };
  }, [ocorrenciasFiltradas]);

  // ── Compliance SLA por mês ────────────────────────────────────────────────────
  const slaCompliancePorMes = useMemo(() => {
    const now = Date.now();
    const map = {};
    ocorrenciasFiltradas.forEach(oc => {
      const label = getMonthLabel(oc.dataAbertura);
      if (!label) return;
      if (!map[label]) map[label] = { dentro:0, fora:0 };
      const abertura = toMs(oc.dataAbertura);
      const fim = oc.status==='concluido' ? toMs(oc.dataConclusao) : now;
      const prazoMs = (slaConfig[resolveOcTipo(oc)]||24)*3600000;
      if ((fim-abertura)<=prazoMs) map[label].dentro++;
      else map[label].fora++;
    });
    return Object.entries(map).slice(-6).map(([mes,v])=>({
      mes,
      dentro:v.dentro,
      fora:v.fora,
      pct: v.dentro+v.fora>0 ? Math.round((v.dentro/(v.dentro+v.fora))*100) : 0,
    }));
  }, [ocorrenciasFiltradas, slaConfig]);

  // ── Distribuição tempo de resolução ──────────────────────────────────────────
  const distResolucao = useMemo(() => {
    const conc = ocorrenciasFiltradas.filter(o=>o.status==='concluido'&&o.dataAbertura&&o.dataConclusao);
    const buckets = { '<1d':0, '1-3d':0, '3-7d':0, '7-14d':0, '>14d':0 };
    conc.forEach(o => {
      const d = getDaysDiff(o.dataAbertura, o.dataConclusao)||0;
      if (d<1) buckets['<1d']++;
      else if (d<=3) buckets['1-3d']++;
      else if (d<=7) buckets['3-7d']++;
      else if (d<=14) buckets['7-14d']++;
      else buckets['>14d']++;
    });
    return Object.entries(buckets).map(([faixa,total])=>({ faixa, total }));
  }, [ocorrenciasFiltradas]);

  // ── Censo: hospitalizações ────────────────────────────────────────────────────
  const censoStats = useMemo(() => {
    if (censoNorm.length === 0) return null;
    const total      = censoNorm.length;
    const ativos     = censoNorm.filter(r=>r._ativo).length;
    const hospRows   = censoNorm.filter(r=>r._isHosp);
    const obitoRows  = censoNorm.filter(r=>r._isObito);
    const cancelRows = censoNorm.filter(r=>r._isCancel);
    const taxaHosp   = (hospRows.length+ativos) > 0
      ? pct(hospRows.length, hospRows.length+ativos)
      : '—';

    // Por operadora
    const opMap = {};
    censoNorm.forEach(r => {
      const op = r._operadora || '—';
      if (!opMap[op]) opMap[op] = { ativos:0, hosp:0, obito:0, total:0 };
      opMap[op].total++;
      if (r._ativo) opMap[op].ativos++;
      if (r._isHosp)  opMap[op].hosp++;
      if (r._isObito) opMap[op].obito++;
    });
    const porOperadora = Object.entries(opMap)
      .map(([op,v])=>({ op, ...v, taxa: v.total>0 ? Math.round((v.hosp/v.total)*100) : 0 }))
      .sort((a,b)=>b.hosp-a.hosp);

    // Por complexidade
    const cxMap = {};
    censoNorm.forEach(r => {
      const cx = r._complexidade || 'Sem info';
      if (!cxMap[cx]) cxMap[cx] = { total:0, hosp:0 };
      cxMap[cx].total++;
      if (r._isHosp) cxMap[cx].hosp++;
    });
    const porComplexidade = Object.entries(cxMap)
      .map(([cx,v])=>({ cx, ...v, taxa: v.total>0 ? Math.round((v.hosp/v.total)*100) : 0 }))
      .sort((a,b)=>b.hosp-a.hosp);

    // Trend mensal (hospitalização por mês de saída)
    const mesMap = {};
    hospRows.forEach(r => {
      if (!r._dSaida) return;
      const label = r._dSaida.toLocaleDateString('pt-BR', { month:'short', year:'2-digit' });
      mesMap[label] = (mesMap[label]||0)+1;
    });
    const tendencia = Object.entries(mesMap).slice(-6).map(([mes,hosp])=>({ mes, hosp }));

    return { total, ativos, hospRows, hospTotal:hospRows.length, obitoTotal:obitoRows.length, cancelTotal:cancelRows.length, taxaHosp, porOperadora, porComplexidade, tendencia };
  }, [censoNorm]);

  // ── Operadora mais frequente nas ocorrências ──────────────────────────────────
  const topOperadoraOc = useMemo(() => {
    const map = {};
    ocorrenciasFiltradas.forEach(oc => {
      const op = nf(oc._pac?.convenio) || oc.paciente?.plano || '—';
      if (op !== '—') map[op] = (map[op]||0)+1;
    });
    const top = Object.entries(map).sort((a,b)=>b[1]-a[1])[0];
    return top ? `${top[0]} (${top[1]})` : '—';
  }, [ocorrenciasFiltradas]);

  const tc = TIPO_CONFIG[tipoAtivo];
  const isHospTab = tipoAtivo === 'hospitalizacao';

  // ── Exportar Excel ────────────────────────────────────────────────────────────
  const exportarExcel = () => {
    const dados = ocorrenciasFiltradas.map(oc => ({
      'Tipo':             tc.label,
      'Paciente':         oc.paciente?.nomeCompleto || oc.nomePaciente || '',
      'Nº IW':            oc._pac?.numIW || oc.paciente?.numIW || oc.numeroIW || '',
      'Nº CAP':           oc._pac?.numero || '',
      'Operadora':        nf(oc._pac?.convenio) || oc.paciente?.plano || '',
      'Filial':           nf(oc._pac?.filial) || '',
      'VM':               oc._pac?.vm ? 'Sim' : '—',
      'Status':           oc.status || '',
      'Vencida SLA':      overdueIds.has(oc.id) ? 'Sim' : 'Não',
      'Data Abertura':    formatDateBR(oc.dataAbertura) || '',
      'Data Conclusão':   formatDateBR(oc.dataConclusao) || '',
      'Dias para Resolver': getDaysDiff(oc.dataAbertura, oc.dataConclusao) ?? '',
      'Criado por':       oc.criadoPorNome || '',
      'Tarefas Pendentes': (tarefasMap[oc.id]||[]).filter(t=>t.status!=='concluido').length,
    }));
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tc.label + 's');
    XLSX.writeFile(wb, `hosp_${tipoAtivo}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportarExcelCenso = () => {
    if (!censoStats) return;
    const dados = censoStats.hospRows.map(r => ({
      'Nome': r._nome,
      'Operadora': r._operadora,
      'Complexidade': r._complexidade,
      'Condição de Saída': r._efCond,
      'Data Entrada': r._dEntrada ? r._dEntrada.toLocaleDateString('pt-BR') : '',
      'Data Saída': r._dSaida ? r._dSaida.toLocaleDateString('pt-BR') : '',
      'Dias em HC': getDaysDiff(r._dEntrada, r._dSaida) ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hospitalizações Censo');
    XLSX.writeFile(wb, `censo_hospitalizacoes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ──────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f4ff 0%,#fafbff 100%)', padding:'24px', fontFamily:"'Inter','Segoe UI',sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <Link to="/gestao-ocorrencias" style={{ color:'#94a3b8', textDecoration:'none', fontSize:'0.8rem', fontWeight:600, display:'block', marginBottom:6 }}>← Gestão de Ocorrências</Link>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <h2 style={{ fontSize:'1.6rem', fontWeight:700, color:'#1a1a2e', margin:0 }}>Dashboard — Ocorrências & Qualidade</h2>
            <span style={{ background:tc.badgeBg, color:'#fff', fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20, letterSpacing:'0.5px', textTransform:'uppercase' }}>
              {tc.emoji} {tc.label.toUpperCase()}
            </span>
            {kpis.vencidas > 0 && (
              <span style={{ background:'#fef2f2', color:'#dc2626', fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, border:'1.5px solid #fca5a5' }}>
                ⚠️ {kpis.vencidas} VENCIDA{kpis.vencidas > 1 ? 'S' : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={()=>setShowSlaModal(true)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:'0.82rem', fontWeight:600, cursor:'pointer', color:'#475569' }}>
            ⚙️ SLA: {slaConfig[tipoAtivo]}h
          </button>
          <button onClick={exportarExcel} style={{ display:'flex', alignItems:'center', gap:5, padding:'9px 16px', background:'#10b981', color:'#fff', border:'none', borderRadius:10, fontSize:'0.85rem', fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(16,185,129,0.28)' }}>
            📥 Excel Ocorrências
          </button>
          {isHospTab && censoStats && (
            <button onClick={exportarExcelCenso} style={{ display:'flex', alignItems:'center', gap:5, padding:'9px 16px', background:'#6366f1', color:'#fff', border:'none', borderRadius:10, fontSize:'0.85rem', fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(99,102,241,0.28)' }}>
              📥 Excel Censo
            </button>
          )}
        </div>
      </div>

      {/* ── FILTROS + KPIs ── */}
      <div style={{ display:'flex', gap:24, marginBottom:28, alignItems:'flex-start' }}>

        {/* Filtros: tipo + período */}
        <div style={{ flexShrink:0, width:260 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:6, background:'#fff', borderRadius:14, padding:8, boxShadow:'0 2px 10px rgba(102,126,234,0.08)', marginBottom:14 }}>
            {Object.entries(TIPO_CONFIG).map(([tipo,cfg])=>(
              <button key={tipo} onClick={()=>setTipoAtivo(tipo)}
                style={{ padding:'10px 18px', borderRadius:10, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, textAlign:'left', transition:'all 0.2s',
                  background: tipoAtivo===tipo ? cfg.color : 'transparent',
                  color: tipoAtivo===tipo ? '#fff' : '#6b7280',
                  boxShadow: tipoAtivo===tipo ? `0 4px 12px ${cfg.color}44` : 'none' }}>
                {cfg.emoji} {cfg.label}
              </button>
            ))}
          </div>
          <div style={{ background:'#fff', borderRadius:12, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', letterSpacing:'0.06em', marginBottom:10 }}>PERÍODO</div>
            {[['DE',dataInicio,setDataInicio],['ATÉ',dataFim,setDataFim]].map(([label,val,setter])=>(
              <div key={label} style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:600, color:'#6b7280', marginBottom:4 }}>{label}</div>
                <input type="date" value={val} onChange={e=>setter(e.target.value)}
                  style={{ width:'100%', padding:'7px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:12, outline:'none', fontFamily:'Inter,sans-serif', boxSizing:'border-box', color:'#334155' }} />
              </div>
            ))}
            {oldestOpenDate && dataInicio !== oldestOpenDate && (
              <button onClick={()=>{ setDataInicio(oldestOpenDate); setDataFim(todayStr()); }}
                style={{ marginTop:6, width:'100%', padding:5, borderRadius:7, border:'1px dashed #d1d5db', background:'transparent', fontSize:10, color:'#6b7280', cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
                ↺ padrão ({oldestOpenDate.split('-').reverse().join('/')})
              </button>
            )}
          </div>
        </div>

        {/* KPI cards */}
        {loading ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', minHeight:160, color:'#6b7280', fontSize:14 }}>⏳ Carregando...</div>
        ) : (
          <div style={{ flex:1 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:12 }}>
              <KpiCard icon="📋" value={kpis.total}       label="Total no Período"   color={tc.color} />
              <KpiCard icon="🔴" value={kpis.emAberto}    label="Em Aberto"          color="#ef4444" />
              <KpiCard icon="🔵" value={kpis.emAndamento} label="Em Andamento"       color="#3b82f6" />
              <KpiCard icon="✅" value={kpis.concluidas}  label="Concluídas"         color="#10b981" />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12 }}>
              <KpiCard icon="⚠️" value={kpis.vencidas}    label={`Vencidas (SLA ${slaConfig[tipoAtivo]}h)`} color="#dc2626" alert />
              <KpiCard icon="⏱️" value={kpis.tempoMedio !== null ? `${kpis.tempoMedio}d` : '—'} label="Tempo Médio Resolução" color="#8b5cf6" />
              <KpiCard icon="🎯" value={kpis.taxaSla !== null ? `${kpis.taxaSla}%` : '—'} label="Dentro do SLA" color="#0ea5e9" sub={`${ocorrenciasFiltradas.length - kpis.vencidas} de ${kpis.total}`} />
              <KpiCard icon="🏆" value={topOperadoraOc} label="Operadora c/ mais casos" color="#f59e0b" />
            </div>
          </div>
        )}
      </div>

      {!loading && (
        <>
          {/* ── ABAS INTERNAS (só quando é hospitalização) ── */}
          {isHospTab && (
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {[
                { key:'ocorrencias', label:'📋 Ocorrências', desc:'Gestão operacional' },
                { key:'censo',       label:'🏥 Censo & Qualidade', desc:'Análise do censo' },
                { key:'lista',       label:'📑 Listagem Detalhada', desc:'Todos os casos' },
              ].map(({ key, label, desc }) => (
                <button key={key} onClick={()=>setAbaHosp(key)}
                  style={{ padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, transition:'all 0.2s',
                    background: abaHosp===key ? tc.color : '#fff',
                    color: abaHosp===key ? '#fff' : '#6b7280',
                    boxShadow: abaHosp===key ? `0 4px 14px ${tc.color}44` : '0 2px 8px rgba(0,0,0,0.06)',
                    border: abaHosp===key ? 'none' : '1px solid #e8ecf4' }}>
                  {label}
                  <span style={{ display:'block', fontSize:'0.65rem', fontWeight:500, marginTop:1, opacity:0.75 }}>{desc}</span>
                </button>
              ))}
            </div>
          )}

          {/* ════════════════════════════════════════════════
              ABA: OCORRÊNCIAS (gestão operacional)
          ════════════════════════════════════════════════ */}
          {(!isHospTab || abaHosp === 'ocorrencias') && (
            <>
              {/* Setor Cards */}
              {setorStats.length > 0 && (
                <div style={{ marginBottom:28 }}>
                  <h3 style={{ fontSize:'0.92rem', fontWeight:700, color:'#374151', margin:'0 0 14px', display:'flex', alignItems:'center', gap:8 }}>
                    🔧 Tarefas por Setor
                    <span style={{ fontSize:11, fontWeight:500, color:'#94a3b8', background:'#f1f5f9', padding:'2px 8px', borderRadius:10 }}>{setorStats.length}</span>
                  </h3>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:14 }}>
                    {setorStats.map(([setor,stats])=>(
                      <SetorCard key={setor} setor={setor} stats={stats} />
                    ))}
                  </div>
                </div>
              )}

              {/* Gráficos */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:20, marginBottom:24 }}>
                {/* Por mês */}
                <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                  <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 16px' }}>📅 {tc.label}s por Mês</h3>
                  {dadosPorMes.length === 0 ? (
                    <div style={{ textAlign:'center', color:'#b0b8c9', padding:'40px 0', fontSize:13 }}>Sem dados no período</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={dadosPorMes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4ff" />
                        <XAxis dataKey="mes" tick={{ fontSize:12 }} />
                        <YAxis tick={{ fontSize:12 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="total" fill={tc.chartColor} radius={[4,4,0,0]} name={tc.label+'s'} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* SLA compliance por mês */}
                <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                  <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 16px' }}>🎯 Compliance SLA por Mês</h3>
                  {slaCompliancePorMes.length === 0 ? (
                    <div style={{ textAlign:'center', color:'#b0b8c9', padding:'40px 0', fontSize:13 }}>Sem dados</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={slaCompliancePorMes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4ff" />
                        <XAxis dataKey="mes" tick={{ fontSize:12 }} />
                        <YAxis tick={{ fontSize:12 }} allowDecimals={false} />
                        <Tooltip formatter={(v,n)=>n==='pct'?`${v}%`:v} />
                        <Bar dataKey="dentro" fill="#10b981" radius={[0,0,0,0]} name="Dentro do SLA" stackId="a" />
                        <Bar dataKey="fora"   fill="#ef4444" radius={[4,4,0,0]} name="Fora do SLA"   stackId="a" />
                        <Legend />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Tempo de resolução */}
                <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                  <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 16px' }}>⏱️ Tempo de Resolução</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={distResolucao}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f4ff" />
                      <XAxis dataKey="faixa" tick={{ fontSize:12 }} />
                      <YAxis tick={{ fontSize:12 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="total" fill="#8b5cf6" radius={[4,4,0,0]} name="Ocorrências" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Perfil: VM x Sem VM */}
                {isHospTab && perfil.linkedCount > 0 && (
                  <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                    <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 6px' }}>🫁 Perfil — Captação Vinculada</h3>
                    <p style={{ fontSize:'0.72rem', color:'#94a3b8', margin:'0 0 16px' }}>{perfil.linkedCount} de {ocorrenciasFiltradas.length} ocorrências com paciente identificado</p>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                      {[
                        { label:'Com VM 🫁', value:perfil.comVM, color:'#ef4444' },
                        { label:'Sem VM', value:perfil.semVM, color:'#3b82f6' },
                      ].map(({label,value,color})=>(
                        <div key={label} style={{ background:'#f8fafc', borderRadius:10, padding:'14px', textAlign:'center', borderTop:`3px solid ${color}` }}>
                          <div style={{ fontSize:'1.8rem', fontWeight:800, color }}>{value}</div>
                          <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#475569', marginBottom:8 }}>Por Operadora</div>
                    {Object.entries(perfil.porOp).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([op,v],i)=>(
                      <HBar key={op} label={op} value={v} max={Math.max(...Object.values(perfil.porOp))} color={COLORS6[i%6]} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════
              ABA: CENSO & QUALIDADE
          ════════════════════════════════════════════════ */}
          {isHospTab && abaHosp === 'censo' && (
            <div>
              {loadingCenso ? (
                <div style={{ textAlign:'center', padding:60, color:'#6b7280' }}>⏳ Carregando dados do censo...</div>
              ) : !censoStats ? (
                <div style={{ background:'#fff', borderRadius:16, padding:40, textAlign:'center', color:'#94a3b8', border:'1px solid #e8ecf4' }}>
                  <div style={{ fontSize:'2rem', marginBottom:12 }}>📊</div>
                  <div style={{ fontWeight:700, marginBottom:6 }}>Nenhum dado de censo disponível</div>
                  <div style={{ fontSize:'0.8rem' }}>Faça o upload de um arquivo do censo em <strong>Upload de Indicadores</strong></div>
                </div>
              ) : (
                <>
                  {/* Banner de período */}
                  <div style={{ background:'linear-gradient(135deg,#667eea,#764ba2)', borderRadius:14, padding:'16px 24px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                    <div>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8, color:'rgba(255,255,255,0.7)', marginBottom:2 }}>Panorama Assistencial — Censo</div>
                      <div style={{ fontSize:'1rem', fontWeight:700, color:'#fff' }}>Análise de hospitalização dos pacientes em home care</div>
                    </div>
                    <div style={{ display:'flex', gap:10 }}>
                      <span style={{ background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:'0.75rem', fontWeight:700, padding:'4px 12px', borderRadius:20 }}>
                        {censoStats.total} pacientes no censo
                      </span>
                      <span style={{ background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:'0.75rem', fontWeight:700, padding:'4px 12px', borderRadius:20 }}>
                        Última atualização: {censoRows.length} linhas
                      </span>
                    </div>
                  </div>

                  {/* KPIs do censo */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:14, marginBottom:24 }}>
                    {[
                      { icon:'🏠', value:censoStats.ativos,      label:'Ativos no Censo',           color:'#10b981' },
                      { icon:'🏥', value:censoStats.hospTotal,   label:'Saídas por Hospitalização', color:'#f97316' },
                      { icon:'📊', value:censoStats.taxaHosp,    label:'Taxa de Hospitalização',    color:'#6366f1', sub:'% de altas por hosp.' },
                      { icon:'⚫', value:censoStats.obitoTotal,  label:'Óbitos',                    color:'#64748b' },
                      { icon:'🚫', value:censoStats.cancelTotal, label:'Cancelamentos',             color:'#8b5cf6' },
                    ].map((k,i)=>(
                      <KpiCard key={i} {...k} />
                    ))}
                  </div>

                  {/* Hospitalizações × Ocorrências: cruzamento */}
                  <div style={{ background:'linear-gradient(135deg,#fff7ed,#fff)', borderRadius:14, padding:'18px 22px', marginBottom:24, border:'1px solid #fed7aa' }}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#ea580c', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                      🔗 Correlação: Censo × Ocorrências abertas
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16 }}>
                      {[
                        { label:'Hospitalizações no censo (desfecho)', value:censoStats.hospTotal, color:'#f97316', desc:'Pacientes que saíram do HC por hospitalização' },
                        { label:'Ocorrências abertas de hospitalização', value:kpis.emAberto + kpis.emAndamento, color:'#3b82f6', desc:'Casos ativos sendo gerenciados agora' },
                        { label:'Média dias aberto (ocorrências)', value:perfil.mediaAberto !== '—' ? `${perfil.mediaAberto}d` : '—', color:'#8b5cf6', desc:'Tempo médio das ocorrências em aberto' },
                      ].map(({label,value,color,desc})=>(
                        <div key={label} style={{ background:'#fff', borderRadius:10, padding:14, border:`1px solid ${color}22` }}>
                          <div style={{ fontSize:'1.8rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
                          <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#374151', marginTop:4, marginBottom:4 }}>{label}</div>
                          <div style={{ fontSize:'0.65rem', color:'#94a3b8', lineHeight:1.4 }}>{desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Charts: tendência + por operadora + por complexidade */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
                    {/* Tendência mensal */}
                    <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                      <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 16px' }}>📈 Hospitalizações por Mês (Censo)</h3>
                      {censoStats.tendencia.length === 0 ? (
                        <div style={{ textAlign:'center', color:'#b0b8c9', padding:'40px 0', fontSize:13 }}>Sem datas de saída registradas</div>
                      ) : (
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={censoStats.tendencia}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f4ff" />
                            <XAxis dataKey="mes" tick={{ fontSize:12 }} />
                            <YAxis tick={{ fontSize:12 }} allowDecimals={false} />
                            <Tooltip />
                            <Line type="monotone" dataKey="hosp" stroke="#f97316" strokeWidth={2} dot={{ r:4 }} name="Hospitalizações" />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Por operadora */}
                    <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                      <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 6px' }}>🏢 Hospitalização por Operadora</h3>
                      <p style={{ fontSize:'0.72rem', color:'#94a3b8', margin:'0 0 16px' }}>Total de saídas por hospitalização por convênio</p>
                      {censoStats.porOperadora.slice(0,8).map((r,i)=>(
                        <HBar key={r.op} label={`${r.op} (${r.taxa}%)`} value={r.hosp} max={censoStats.porOperadora[0]?.hosp||1} color={COLORS6[i%6]} />
                      ))}
                    </div>
                  </div>

                  {/* Por complexidade + tabela operadora */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
                    {/* Por complexidade */}
                    <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                      <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 6px' }}>🔬 Hospitalização por Complexidade</h3>
                      <p style={{ fontSize:'0.72rem', color:'#94a3b8', margin:'0 0 16px' }}>Número e % de pacientes hospitalizados por nível</p>
                      {censoStats.porComplexidade.slice(0,6).map((r,i)=>(
                        <div key={r.cx} style={{ marginBottom:12 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                            <span style={{ fontSize:'0.78rem', color:'#475569', fontWeight:600 }}>{r.cx||'Sem info'}</span>
                            <span style={{ fontSize:'0.78rem', color:'#1e293b', fontWeight:700 }}>{r.hosp} <span style={{ color:'#94a3b8', fontWeight:400 }}>({r.taxa}%)</span></span>
                          </div>
                          <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${r.taxa}%`, background:COLORS6[i%6], borderRadius:4 }} />
                          </div>
                          <div style={{ fontSize:'0.62rem', color:'#94a3b8', marginTop:2 }}>{r.total} pacientes nessa complexidade</div>
                        </div>
                      ))}
                    </div>

                    {/* Tabela detalhada por operadora */}
                    <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4', overflowX:'auto' }}>
                      <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:'0 0 16px' }}>📊 Detalhamento por Operadora</h3>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                        <thead>
                          <tr style={{ background:'#f8fafc' }}>
                            {['Operadora','Ativos','Hosp.','Óbitos','Taxa Hosp.'].map(h=>(
                              <th key={h} style={{ padding:'8px 10px', textAlign:'left', borderBottom:'2px solid #e2e8f0', fontWeight:700, color:'#475569', fontSize:'0.72rem' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {censoStats.porOperadora.slice(0,10).map((r,i)=>(
                            <tr key={r.op} style={{ background:i%2===0?'#fff':'#f8fafc' }}>
                              <td style={{ padding:'8px 10px', fontWeight:600, color:'#1e293b', borderBottom:'1px solid #f1f5f9' }}>{r.op}</td>
                              <td style={{ padding:'8px 10px', color:'#10b981', fontWeight:700, borderBottom:'1px solid #f1f5f9' }}>{r.ativos}</td>
                              <td style={{ padding:'8px 10px', color:'#f97316', fontWeight:700, borderBottom:'1px solid #f1f5f9' }}>{r.hosp}</td>
                              <td style={{ padding:'8px 10px', color:'#64748b', fontWeight:600, borderBottom:'1px solid #f1f5f9' }}>{r.obito}</td>
                              <td style={{ padding:'8px 10px', borderBottom:'1px solid #f1f5f9' }}>
                                <span style={{ background:r.taxa>15?'#fef2f2':r.taxa>8?'#fff7ed':'#f0fdf4', color:r.taxa>15?'#dc2626':r.taxa>8?'#ea580c':'#16a34a', padding:'2px 8px', borderRadius:12, fontWeight:700, fontSize:'0.72rem' }}>
                                  {r.taxa}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Lista de pacientes hospitalizados do censo */}
                  <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                      <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:0 }}>
                        🏥 Pacientes Saídos por Hospitalização — Censo
                        <span style={{ marginLeft:8, background:'#fff7ed', color:'#f97316', fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:10 }}>
                          {censoStats.hospTotal}
                        </span>
                      </h3>
                      <button onClick={exportarExcelCenso} style={{ padding:'6px 14px', background:'#f97316', color:'#fff', border:'none', borderRadius:8, fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
                        📥 Exportar
                      </button>
                    </div>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                        <thead>
                          <tr style={{ background:'#fff7ed' }}>
                            {['Nome','Operadora','Complexidade','Condição de Saída','Entrada','Saída','Dias'].map(h=>(
                              <th key={h} style={{ padding:'8px 10px', textAlign:'left', borderBottom:'2px solid #fed7aa', fontWeight:700, color:'#92400e', fontSize:'0.72rem', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {censoStats.hospRows.slice(0,30).map((r,i)=>(
                            <tr key={i} style={{ background:i%2===0?'#fff':'#fafafa' }}>
                              <td style={{ padding:'8px 10px', fontWeight:600, color:'#1e293b', borderBottom:'1px solid #f1f5f9', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r._nome||'—'}</td>
                              <td style={{ padding:'8px 10px', color:'#475569', borderBottom:'1px solid #f1f5f9' }}>{r._operadora||'—'}</td>
                              <td style={{ padding:'8px 10px', color:'#475569', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap' }}>{r._complexidade||'—'}</td>
                              <td style={{ padding:'8px 10px', borderBottom:'1px solid #f1f5f9' }}>
                                <span style={{ background:'#fff7ed', color:'#ea580c', padding:'2px 8px', borderRadius:12, fontWeight:600, fontSize:'0.7rem', whiteSpace:'nowrap' }}>
                                  {r._efCond||'—'}
                                </span>
                              </td>
                              <td style={{ padding:'8px 10px', color:'#64748b', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap' }}>{r._dEntrada?r._dEntrada.toLocaleDateString('pt-BR'):'—'}</td>
                              <td style={{ padding:'8px 10px', color:'#64748b', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap' }}>{r._dSaida?r._dSaida.toLocaleDateString('pt-BR'):'—'}</td>
                              <td style={{ padding:'8px 10px', fontWeight:700, color:'#f97316', borderBottom:'1px solid #f1f5f9' }}>{getDaysDiff(r._dEntrada,r._dSaida)??'—'}</td>
                            </tr>
                          ))}
                          {censoStats.hospRows.length > 30 && (
                            <tr>
                              <td colSpan={7} style={{ padding:'10px', textAlign:'center', color:'#94a3b8', fontSize:'0.75rem', fontStyle:'italic' }}>
                                + {censoStats.hospRows.length - 30} pacientes (exporte o Excel para ver todos)
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════
              ABA: LISTAGEM DETALHADA
          ════════════════════════════════════════════════ */}
          {(!isHospTab || abaHosp === 'lista') && (
            <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #e8ecf4' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'#2d3748', margin:0 }}>
                  📑 Listagem de {tc.label}s
                  <span style={{ marginLeft:8, background:'#f1f5f9', color:'#475569', fontSize:'0.72rem', fontWeight:600, padding:'2px 8px', borderRadius:10 }}>
                    {ocorrenciasFiltradas.length}
                  </span>
                </h3>
                <button onClick={exportarExcel} style={{ padding:'6px 14px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
                  📥 Exportar Excel
                </button>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      {['','Paciente','Nº IW','Operadora','VM','Abertura','Status','SLA','Tarefas','Dias Aberto'].map(h=>(
                        <th key={h} style={{ padding:'9px 10px', textAlign:'left', borderBottom:'2px solid #e2e8f0', fontWeight:700, color:'#475569', fontSize:'0.72rem', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ocorrenciasFiltradas.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding:'40px', textAlign:'center', color:'#94a3b8' }}>Nenhuma ocorrência no período</td></tr>
                    ) : ocorrenciasFiltradas
                      .sort((a,b) => toMs(b.dataAbertura) - toMs(a.dataAbertura))
                      .map((oc, i) => {
                        const aberto = rowAberta === oc.id;
                        const isOverdue = overdueIds.has(oc.id);
                        const pac = oc._pac;
                        const abertura = oc.dataAbertura ? (oc.dataAbertura.toDate ? oc.dataAbertura.toDate() : new Date(oc.dataAbertura)) : null;
                        const diasAberto = abertura ? Math.round((Date.now()-abertura.getTime())/(1000*60*60*24)) : '—';
                        const tarefas = tarefasMap[oc.id] || [];
                        const pendentes = tarefas.filter(t=>t.status!=='concluido').length;
                        const statusCor = oc.status==='concluido'?'#10b981':oc.status==='em_andamento'?'#3b82f6':'#ef4444';
                        const statusLabel = oc.status==='concluido'?'Concluída':oc.status==='em_andamento'?'Em andamento':'Em aberto';
                        return (
                          <React.Fragment key={oc.id}>
                            <tr style={{ background:i%2===0?'#fff':'#f8fafc', cursor:'pointer' }} onClick={()=>setRowAberta(aberto?null:oc.id)}>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', color:'#94a3b8', fontSize:'0.68rem' }}>
                                {aberto ? '▲' : '▼'}
                              </td>
                              <td style={{ padding:'10px 10px', fontWeight:600, color:'#1e293b', borderBottom:'1px solid #f1f5f9', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {pac?.nome || oc.paciente?.nomeCompleto || oc.nomePaciente || '—'}
                                {pac && <span style={{ display:'block', fontSize:'0.62rem', color:'#94a3b8', fontWeight:400 }}>{pac.numero}</span>}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', fontWeight:700, color:'#6366f1' }}>
                                {pac?.numIW || oc.paciente?.numIW || '—'}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', color:'#475569', whiteSpace:'nowrap' }}>
                                {nf(pac?.convenio) || oc.paciente?.plano || '—'}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9' }}>
                                {pac?.vm
                                  ? <span style={{ background:'#fef2f2', color:'#ef4444', padding:'2px 6px', borderRadius:8, fontWeight:700, fontSize:'0.68rem' }}>VM</span>
                                  : <span style={{ color:'#94a3b8', fontSize:'0.72rem' }}>—</span>}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', color:'#64748b', whiteSpace:'nowrap' }}>
                                {formatDateBR(oc.dataAbertura) || '—'}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9' }}>
                                <span style={{ background:`${statusCor}15`, color:statusCor, padding:'3px 8px', borderRadius:12, fontWeight:700, fontSize:'0.7rem', whiteSpace:'nowrap' }}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9' }}>
                                {isOverdue
                                  ? <span style={{ background:'#fef2f2', color:'#dc2626', padding:'3px 8px', borderRadius:12, fontWeight:700, fontSize:'0.7rem' }}>⚠️ Vencida</span>
                                  : <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'3px 8px', borderRadius:12, fontWeight:700, fontSize:'0.7rem' }}>✓ OK</span>}
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', textAlign:'center' }}>
                                <span style={{ background:pendentes>0?'#fff7ed':'#f0fdf4', color:pendentes>0?'#ea580c':'#16a34a', padding:'3px 8px', borderRadius:12, fontWeight:700, fontSize:'0.72rem' }}>
                                  {pendentes} pend.
                                </span>
                              </td>
                              <td style={{ padding:'10px 10px', borderBottom:'1px solid #f1f5f9', fontWeight:700, color: oc.status!=='concluido'&&Number(diasAberto)>7?'#ef4444':'#475569' }}>
                                {typeof diasAberto === 'number' ? `${diasAberto}d` : '—'}
                              </td>
                            </tr>
                            {/* Linha expandida */}
                            {aberto && (
                              <tr>
                                <td colSpan={10} style={{ padding:0, background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                                  <div style={{ padding:'16px 20px' }}>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                                      {/* Detalhes do paciente */}
                                      <div>
                                        <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.6, marginBottom:8 }}>Paciente (Captação)</div>
                                        {pac ? (
                                          <div style={{ fontSize:'0.78rem', color:'#475569', lineHeight:1.8 }}>
                                            <div><strong>Nome:</strong> {pac.nome}</div>
                                            <div><strong>Nº IW:</strong> {pac.numIW||'—'}</div>
                                            <div><strong>Convênio:</strong> {nf(pac.convenio)||'—'}</div>
                                            <div><strong>Filial:</strong> {nf(pac.filial)||'—'}</div>
                                            <div><strong>VM:</strong> {pac.vm?'Sim':'Não'}</div>
                                            <div><strong>Complexidade:</strong> {pac.complexidade||'—'}</div>
                                            <div><strong>Cidade:</strong> {pac.cidade||'—'}</div>
                                            <div><strong>Status Captação:</strong> {pac.status||'—'}</div>
                                          </div>
                                        ) : (
                                          <div style={{ fontSize:'0.75rem', color:'#94a3b8', fontStyle:'italic' }}>
                                            Paciente não vinculado à captação.<br/>
                                            Nome: {oc.paciente?.nomeCompleto || oc.nomePaciente || '—'}<br/>
                                            Nº IW: {oc.paciente?.numIW || '—'}
                                          </div>
                                        )}
                                      </div>
                                      {/* Dados da ocorrência */}
                                      <div>
                                        <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.6, marginBottom:8 }}>Ocorrência</div>
                                        <div style={{ fontSize:'0.78rem', color:'#475569', lineHeight:1.8 }}>
                                          <div><strong>Abertura:</strong> {formatDateBR(oc.dataAbertura)||'—'}</div>
                                          <div><strong>Conclusão:</strong> {formatDateBR(oc.dataConclusao)||'—'}</div>
                                          <div><strong>Criado por:</strong> {oc.criadoPorNome||'—'}</div>
                                          <div><strong>ID:</strong> <span style={{ fontFamily:'monospace', fontSize:'0.68rem' }}>{oc.id}</span></div>
                                        </div>
                                      </div>
                                      {/* Tarefas */}
                                      <div>
                                        <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.6, marginBottom:8 }}>Tarefas ({tarefas.length})</div>
                                        {tarefas.length === 0 ? (
                                          <div style={{ fontSize:'0.75rem', color:'#94a3b8', fontStyle:'italic' }}>Sem tarefas</div>
                                        ) : (
                                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                                            {tarefas.map(t => {
                                              const tCor = t.status==='concluido'?'#10b981':t.status==='em_andamento'?'#3b82f6':'#f59e0b';
                                              return (
                                                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px', background:'#fff', borderRadius:6, border:'1px solid #f1f5f9' }}>
                                                  <span style={{ width:8, height:8, borderRadius:'50%', background:tCor, flexShrink:0 }} />
                                                  <span style={{ fontSize:'0.73rem', color:'#374151', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.area||t.areaPai||'—'}</span>
                                                  <span style={{ fontSize:'0.62rem', color:tCor, fontWeight:700, whiteSpace:'nowrap' }}>{t.status==='concluido'?'OK':t.status==='em_andamento'?'And.':'Pend.'}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showSlaModal && <SlaModal slaConfig={slaConfig} onSave={handleSaveSla} onClose={()=>setShowSlaModal(false)} />}
    </div>
  );
}

export default DashboardHospitalizacao;
